/**
 * Aspect Admin — Audit log / system events.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, toast, openDrawer } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { AUDIT } from "../mock_data.js";

let state = { sev: "all", query: "" };

function sevBadge(sev) {
  const map = { info: ["mute", "Info"], warn: ["warn", "Warning"], err: ["err", "Security"], ok: ["ok", "Success"] };
  const [kind, label] = map[sev] ?? ["mute", sev];
  return `<span class="ap-badge ap-badge--${kind}">${t(label)}</span>`;
}

function auditRow(a) {
  return `
    <tr data-row data-audit="${a.id}">
      <td style="white-space:nowrap;">${t(a.time)}</td>
      <td>${sevBadge(a.sev)}</td>
      <td><span class="ap-codechip" style="padding:0.15rem 0.5rem;">${a.action}</span></td>
      <td class="ap-cell-main" style="font-weight:550;">${escapeHtml(t(a.actor))}</td>
      <td style="max-width:340px;">${escapeHtml(t(a.detail))}</td>
      <td><span class="ap-mono">${t(a.ip)}</span></td>
    </tr>
  `;
}

function auditDrawer(root, a) {
  openDrawer(root, {
    title: a.action,
    subtitle: `${t(a.time)} · ${t(a.actor)}`,
    bodyHtml: `
      <div class="ap-drawer__section">
        <h4>${t("Event")}</h4>
        <dl class="ap-def">
          <dt>${t("Severity")}</dt><dd>${sevBadge(a.sev)}</dd>
          <dt>${t("Action")}</dt><dd><span class="ap-codechip" style="padding:0.15rem 0.5rem;">${a.action}</span></dd>
          <dt>${t("Actor")}</dt><dd>${escapeHtml(t(a.actor))}</dd>
          <dt>${t("Source IP")}</dt><dd><span class="ap-mono">${t(a.ip)}</span></dd>
          <dt>${t("Timestamp")}</dt><dd>${t(a.time)}</dd>
        </dl>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Detail")}</h4>
        <p style="margin:0;font-size:0.84rem;color:var(--ap-slate);line-height:1.55;">${escapeHtml(t(a.detail))}</p>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Raw payload")}</h4>
        <pre class="ap-mono" style="margin:0;padding:0.9rem 1rem;background:rgba(13,41,58,0.045);border:1px solid var(--ap-hairline);border-radius:10px;overflow-x:auto;font-size:0.72rem;line-height:1.6;">{
  "event_id": "${a.id}_${Math.random().toString(36).slice(2, 8)}",
  "action": "${a.action}",
  "actor": "${escapeHtml(a.actor)}",
  "ip": "${a.ip}",
  "workspace": "northwind",
  "severity": "${a.sev}"
}</pre>
      </div>
    `,
    footHtml: `<button type="button" class="ap-btn" data-copy-evt>${icon("copy", 15)} ${t("Copy event JSON")}</button>`,
  });
  root.querySelector("#ap-overlay-slot")?.querySelector("[data-copy-evt]")?.addEventListener("click", () => toast(root, t("Event JSON copied"), "ok"));
}

export function renderAudit(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const q = state.query.trim().toLowerCase();
  const list = AUDIT.filter(
    (a) => (state.sev === "all" || a.sev === state.sev) && (!q || `${a.actor} ${a.action} ${a.detail}`.toLowerCase().includes(q))
  );

  const content = `
    ${pageHead(t("Audit log"), t("Immutable record of every administrative and system event."), `
      <button type="button" class="ap-btn" id="au-stream">${icon("webhook", 15)} ${t("Stream to SIEM")}</button>
      <button type="button" class="ap-btn" id="au-export">${icon("download", 15)} ${t("Export")}</button>
    `)}

    <div class="ap-toolbar">
      <div class="ap-seg">
        ${[["all", "All"], ["info", "Info"], ["ok", "Success"], ["warn", "Warnings"], ["err", "Security"]]
          .map(([id, label]) => `<button type="button" class="ap-seg__btn ${state.sev === id ? "ap-seg__btn--on" : ""}" data-sev="${id}">${t(label)}</button>`)
          .join("")}
      </div>
      <div class="ap-toolbar__spacer"></div>
      <div class="ap-searchfield" style="width:260px;">
        ${icon("search", 15)}
        <input class="ap-input" id="au-search" type="text" placeholder="${t("Search actor, action, detail…")}" value="${escapeHtml(state.query)}" />
      </div>
    </div>

    <div class="ap-panel">
      <div class="ap-tablewrap ap-scroll">
        <table class="ap-table">
          <thead><tr><th>${t("Time")}</th><th>${t("Severity")}</th><th>${t("Action")}</th><th>${t("Actor")}</th><th>${t("Detail")}</th><th>IP</th></tr></thead>
          <tbody>${list.map(auditRow).join("")}</tbody>
        </table>
      </div>
      <div class="ap-panel__foot">
        <span>${t("{n} events · retained 12 months on Scale", { n: list.length })}</span>
        <span>${t("Events are cryptographically chained and tamper-evident")}</span>
      </div>
    </div>
  `;

  const el = renderShell(root, "/audit", content);
  if (!el) return;

  el.querySelectorAll("[data-sev]").forEach((b) =>
    b.addEventListener("click", () => {
      state = { ...state, sev: b.dataset.sev };
      renderAudit(root);
    })
  );

  const search = el.querySelector("#au-search");
  search?.addEventListener("input", () => {
    state = { ...state, query: search.value };
    clearTimeout(search._t);
    search._t = setTimeout(() => {
      const pos = search.selectionStart;
      renderAudit(root);
      const s2 = root.querySelector("#au-search");
      s2?.focus();
      s2?.setSelectionRange(pos, pos);
    }, 240);
  });

  el.querySelector("#au-export")?.addEventListener("click", () => toast(root, t("Audit export queued — link arrives by email"), "ok"));
  el.querySelector("#au-stream")?.addEventListener("click", () => navigate("/integrations"));

  el.querySelector("tbody")?.addEventListener("click", (e) => {
    const row = e.target.closest("[data-audit]");
    if (!row) return;
    const a = AUDIT.find((x) => x.id === row.dataset.audit);
    if (a) auditDrawer(root, a);
  });
}
